import { useCallback } from "react";

import { useDeepMemo } from "../../utils/useDeepMemo";
import { PickerFilteredItems, PickerItem } from "./Picker.types";

function flatten<T extends string, U extends string>(
    items: PickerFilteredItems<T, U>,
): PickerItem<T, U>[] {
    if (items instanceof Set) {
        return [...items.values()];
    } else {
        return [...items.values()].flatMap(it => [...it.values()]);
    }
}

export function useIndexedItem<TTab extends string, TGroup extends string>(
    index: number,
    items: PickerFilteredItems<TTab, TGroup>,
    setIndex: (index: number) => void,
): [PickerItem<TTab, TGroup> | null, (item: PickerItem<TTab, TGroup>) => void] {
    const indexedItem = useDeepMemo((): PickerItem<TTab, TGroup> | null => {
        return flatten(items)[index] ?? null;
    }, [items, index]);

    const setIndexedItem = useCallback(
        (item: PickerItem<TTab, TGroup>) => {
            setIndex(flatten(items).indexOf(item));
        },
        [items, setIndex],
    );

    return [indexedItem, setIndexedItem];
}
